import React, { useEffect, useRef } from "react"
import styled, { css } from "styled-components"
import { useReviewActive } from "../../api/review/hooks"
import { showConfirm } from "../../reducer/confirmSlice"
import { useDispatch } from "react-redux"
import { showAlert } from "../../reducer/alertSlice"
import StatusBadge from "../atoms/StatusBadge"

const RevieweeList = ({ children, ...props }) => {
    const {
        idx,
        activeRevieweeIdx,
        revieweeList,
        setActiveReviewee,
        reviewAnswerData,
        setReviewAnswerData,
        reviewAnswerDefaultData,
        revieweeListRefetch,
        isRevieweeSelectable,
    } = props
    const dispatch = useDispatch()
    const { mutate: reviewActiveMutate } = useReviewActive()
    const isAdminPage = window.location.pathname.includes("admin")
    const itemRefs = useRef({})

    const revieweeBtnClickHandler = (idx) => {
        if (activeRevieweeIdx === idx) return

        const clearForm = () => {
            setActiveReviewee(idx)
            setReviewAnswerData([
                {
                    question_idx: "",
                    option_idx: null,
                    answer_text: "",
                },
            ])
        }

        const isEdited =
            JSON.stringify(reviewAnswerData) !==
            JSON.stringify(reviewAnswerDefaultData)
        if (isEdited) {
            dispatch(
                showConfirm({
                    msg: "입력한 내용이 저장되지 않습니다.\n다른 리뷰 대상으로 넘어갈까요?",
                    func: clearForm,
                })
            )
        } else {
            clearForm()
        }
    }

    const revieweeActiveHandler = (e, el) => {
        e.stopPropagation()

        const activeItems =
            revieweeList?.data?.filter((item) => item.is_active === "1") || []
        const isDeactivating = el.is_active === "1"
        const clickedIdx = el.user.reviewee_idx

        if (isDeactivating && activeItems.length === 1) {
            dispatch(
                showAlert({
                    msg: "리뷰 대상자가 최소 한 명은 있어야 합니다.",
                })
            )
            return
        }

        reviewActiveMutate(
            {
                reviewIdx: idx,
                revieweeIdx: clickedIdx,
                isActive: isDeactivating ? "0" : "1",
            },
            {
                onSuccess: async () => {
                    const response = await revieweeListRefetch()
                    const updatedItems = response?.data?.data || []

                    if (isDeactivating && activeRevieweeIdx === clickedIdx) {
                        const firstActiveItem = updatedItems.find(
                            (i) => i.is_active === "1"
                        )
                        if (firstActiveItem) {
                            setActiveReviewee(firstActiveItem.user.reviewee_idx)
                        }
                    }
                },
            }
        )
    }

    useEffect(() => {
        const ref = itemRefs.current[activeRevieweeIdx]
        if (ref?.scrollIntoView) {
            ref.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }
    }, [activeRevieweeIdx])

    const onImageError = (e) => {
        e.currentTarget.onerror = null
        e.currentTarget.src = "/image/icn_profile_image_default.svg"
    }

    if (revieweeList?.data?.length === 0) {
        return null
    }

    if (isRevieweeSelectable === "0") {
        return (
            <CustomRevieweeList>
                <SectionContainer>
                    <Title>리뷰 대상</Title>
                    <Section>
                        {revieweeList?.data?.map((el) => {
                            const isSelected =
                                activeRevieweeIdx === el.user.reviewee_idx

                            return (
                                <Wrap
                                    key={el.user.reviewee_idx}
                                    ref={(r) => {
                                        if (r)
                                            itemRefs.current[
                                                el.user.reviewee_idx
                                            ] = r
                                    }}
                                >
                                    <RevieweeItem
                                        selected={isSelected}
                                        onClick={() =>
                                            revieweeBtnClickHandler(
                                                el.user.reviewee_idx
                                            )
                                        }
                                    >
                                        <UserInfo>
                                            <ProfileImage
                                                src={
                                                    process.env
                                                        .REACT_APP_RES_PATH +
                                                    el.user.profile_image_url
                                                }
                                                onError={onImageError}
                                                alt={`${el.user.name} profile`}
                                            />
                                            <UserInfoText>
                                                <UserName>
                                                    {el.user.name}
                                                </UserName>
                                                <StatusBadge step={el.is_completed} />
                                            </UserInfoText>
                                        </UserInfo>
                                        <Controls>
                                            {isAdminPage && (
                                                <ScoreAvg>{el.avg}점</ScoreAvg>
                                            )}
                                            {/* 활성/비활성 버튼 제거 */}
                                        </Controls>
                                    </RevieweeItem>
                                </Wrap>
                            )
                        })}
                    </Section>
                </SectionContainer>
            </CustomRevieweeList>
        )
    }

    return (
        <CustomRevieweeList>
            <SectionContainer>
                <Title>리뷰 대상</Title>
                <Section>
                    {revieweeList?.data?.map((el) => {
                        if (el.is_active !== "1") return null
                        const isSelected =
                            activeRevieweeIdx === el.user.reviewee_idx

                        return (
                            <Wrap
                                key={el.user.reviewee_idx}
                                ref={(r) => {
                                    if (r)
                                        itemRefs.current[el.user.reviewee_idx] =
                                            r
                                }}
                            >
                                <RevieweeItem
                                    selected={isSelected}
                                    onClick={() =>
                                        revieweeBtnClickHandler(
                                            el.user.reviewee_idx
                                        )
                                    }
                                >
                                    <UserInfo>
                                        <ProfileImage
                                            src={
                                                process.env.REACT_APP_RES_PATH +
                                                el.user.profile_image_url
                                            }
                                            onError={onImageError}
                                            alt={`${el.user.name} profile`}
                                        />
                                        <UserInfoText>
                                            <UserName>{el.user.name}</UserName>
                                            <StatusBadge step={el.is_completed} />
                                        </UserInfoText>
                                    </UserInfo>
                                    <Controls>
                                        {isAdminPage && (
                                            <ScoreAvg>{el.avg}점</ScoreAvg>
                                        )}
                                        <ActiveButton
                                            type="button"
                                            onClick={(e) =>
                                                revieweeActiveHandler(e, el)
                                            }
                                        >
                                            <Icon className="bi-dash" />
                                        </ActiveButton>
                                    </Controls>
                                </RevieweeItem>
                            </Wrap>
                        )
                    })}
                </Section>
            </SectionContainer>

            <SectionContainer style={{ marginTop: "48px" }}>
                <Title>리뷰 제외 대상</Title>
                <Section>
                    {revieweeList?.data?.map((el) => {
                        if (el.is_active !== "0") return null

                        return (
                            <Wrap key={el.user.reviewee_idx}>
                                <RevieweeItem disabled>
                                    <UserInfo>
                                        <ProfileImage
                                            disabled
                                            src={
                                                process.env.REACT_APP_RES_PATH +
                                                el.user.profile_image_url
                                            }
                                            onError={onImageError}
                                            alt={`${el.user.name} profile`}
                                        />
                                        <UserInfoText>
                                            <UserName disabled>
                                                {el.user.name}
                                            </UserName>
                                        </UserInfoText>
                                    </UserInfo>
                                    <Controls>
                                        <ActiveButton
                                            type="button"
                                            onClick={(e) =>
                                                revieweeActiveHandler(e, el)
                                            }
                                        >
                                            <Icon className="bi-plus" />
                                        </ActiveButton>
                                    </Controls>
                                </RevieweeItem>
                            </Wrap>
                        )
                    })}
                </Section>
            </SectionContainer>
        </CustomRevieweeList>
    )
}

const CustomRevieweeList = styled.div`
    width: 100%;
    max-width: 286px;
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    position: sticky;
    top: 30px;
    left: 0;
`

const SectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
`

const Title = styled.h6`
    color: var(--black, black);
    font-size: 22px;
    font-family: "Pretendard", sans-serif;
    font-weight: 700;
    line-height: 22px;
    padding: 14px 0;
`

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const Wrap = styled.div`
    width: 100%;
`

const RevieweeItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 86px;
    padding: 0 16px;
    border-radius: 8px;
    border: 1px solid transparent;
    background-color: ${({ selected }) => selected ? "var(--bg-light)" : "white"};
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s, height 0.2s;
    box-sizing: border-box;

    &:hover {
        background-color: var(--bg-light);
    }

    ${({ disabled }) =>
        disabled &&
        css`
            height: 68px;
            cursor: default;
            background-color: white !important;
            &:hover {
                background-color: white;
            }
        `}
`

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`

const ProfileImage = styled.img`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    filter: ${(props) => (props.disabled ? "grayscale(100%)" : "none")};
`

const UserInfoText = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
`

const UserName = styled.div`
    color: var(--black, black);
    font-size: 16px;
    font-family: "Gotham", sans-serif;
    font-weight: 500;
    line-height: 16px;

    ${({ disabled }) =>
        disabled &&
        css`
            color: var(--gray3, #999999);
            text-decoration: none;
        `}
`

const Controls = styled.div`
    display: flex;
    align-items: center;
`

const ScoreAvg = styled.span`
    font-size: 1.1em;
    font-weight: 600;
    color: #888888;
    margin-right: 12px;
`

const ActiveButton = styled.button`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 36px;
    height: 36px;
    background: white;
    border-radius: 50%;
    border: none;
    outline: 1px var(--divider_light, #ededed) solid;
    outline-offset: -1px;
    cursor: pointer;
    transition: background-color 0.2s;
    flex-shrink: 0;

    &:hover {
        background-color: #f0f0f0;
    }
`

const Icon = styled.i.attrs(({ className }) => ({
    className: `bi ${className || ""}`.trim(),
}))`
    color: #444444;
    font-size: 1.5rem;
    line-height: 1;
`

export default RevieweeList